import {
  registry,
  type ArgSpec,
  type ClientModule,
  type EventDescriptor,
} from "../registry";
import { createLogger } from "./logger";
import { buildArgs } from "./requestRunner";
import { extractContactId } from "./contactId";
import type { SdkGateway } from "./gateway";
import type { EventLog } from "./eventLog";

const log = createLogger("subscriptions");

const keyOf = (moduleId: string, eventName: string): string =>
  `${moduleId}:${eventName}`;

/**
 * Names of the declared args a caller left unresolved. `buildArgs` drops trailing `undefined`
 * values, so an out-of-range index is itself an unresolved arg. A required arg resolving to
 * `undefined` is fatal: the SDK would key the subscription on `undefined` and the handler would
 * never fire, while the kit reported the event as wired.
 */
function missingRequiredArgs(
  spec: readonly ArgSpec[],
  resolved: readonly unknown[],
): string[] {
  return spec
    .filter((a, i) => a.required === true && resolved[i] === undefined)
    .map((a) => a.k);
}

/**
 * Whether an event can only be subscribed with caller-supplied values: a required arg that no
 * descriptor default can fill. `subscribeAll` skips these instead of registering them dead.
 */
function needsCallerArgs(e: EventDescriptor): boolean {
  const spec = e.args ?? [];
  return missingRequiredArgs(spec, buildArgs(spec, {})).length > 0;
}

/** Positional comparison of two resolved arg lists, so a re-subscribe can detect a new scope. */
function sameArgs(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((v, i) => Object.is(v, b[i]));
}

/** One live subscription: the handler installed on the client, and the extra args used to
 * install it (e.g. a required contactId scope), stored so `off` can be called symmetrically. */
type ActiveSubscription = {
  moduleId: string;
  eventName: string;
  handler: (payload: unknown) => void;
  extraArgs: unknown[];
};

/** A module/event/client triple that has been proven to exist and be callable. */
type ResolvedTarget = {
  mod: ClientModule;
  args: readonly ArgSpec[];
  client: Record<string, unknown>;
};

/**
 * Owns every active event subscription and guarantees exactly one handler per event. Some
 * events declare required extra positional args after the handler (e.g.
 * `onAIAgentContactReady(handler, contactId)`, amendment context) — `subscribe` forwards those
 * so the subscription is never silently keyed by `undefined`, and REFUSES to subscribe at all
 * when a required arg has no value, rather than reporting a dead subscription as active.
 */
export class SubscriptionManager {
  private readonly active = new Map<string, ActiveSubscription>();
  private readonly eventCounts = new Map<string, number>();

  constructor(
    private readonly gateway: SdkGateway,
    private readonly eventLog: EventLog,
  ) {}

  /**
   * Subscribes to one event. `values` supplies any extra positional args the event descriptor
   * declares (e.g. `{ contactId: "c1" }` for `onAIAgentContactReady`). Re-subscribing an active
   * key with identical args is a no-op returning true; with DIFFERENT args it unsubscribes and
   * re-installs against the new scope, so a caller can never believe it re-scoped when it did
   * not. Returns false when the module, event, or client method is unknown/unavailable, or when
   * a required arg resolved to `undefined` — in that case nothing is installed.
   */
  subscribe(
    moduleId: string,
    eventName: string,
    values: Record<string, unknown> = {},
  ): boolean {
    const target = this.resolve(moduleId, eventName);
    if (!target) {
      log.warn("cannot subscribe", { moduleId, eventName });
      return false;
    }

    const extraArgs = buildArgs(target.args, values);
    const missing = missingRequiredArgs(target.args, extraArgs);
    if (missing.length > 0) {
      log.warn(
        "refusing to subscribe: required event args are unset; the SDK would key the " +
          "subscription on undefined and the handler would never fire",
        { moduleId, eventName, missing },
      );
      return false;
    }

    const key = keyOf(moduleId, eventName);
    const existing = this.active.get(key);
    if (existing) {
      if (sameArgs(existing.extraArgs, extraArgs)) return true;
      this.unsubscribe(moduleId, eventName);
    }
    this.install(moduleId, eventName, target.client, extraArgs);
    return true;
  }

  /** Resolves a module/event pair to a callable client method, or undefined if unavailable. */
  private resolve(
    moduleId: string,
    eventName: string,
  ): ResolvedTarget | undefined {
    const mod = registry.find((m) => m.id === moduleId);
    const descriptor = mod?.events.find((e) => e.name === eventName);
    const client = this.gateway.getClient(moduleId);
    if (!mod || !descriptor || !client) return undefined;
    if (typeof client[eventName] !== "function") return undefined;
    return { mod, args: descriptor.args ?? [], client };
  }

  /** Installs the recording handler on the client and records the subscription as active. */
  private install(
    moduleId: string,
    eventName: string,
    client: Record<string, unknown>,
    extraArgs: unknown[],
  ): void {
    const key = keyOf(moduleId, eventName);
    const handler = this.makeHandler(moduleId, eventName, key);
    (client[eventName] as (h: unknown, ...rest: unknown[]) => void)(
      handler,
      ...extraArgs,
    );
    this.active.set(key, { moduleId, eventName, handler, extraArgs });
  }

  /** Builds the recording handler for one event key, isolated from the event log's own errors. */
  private makeHandler(
    moduleId: string,
    eventName: string,
    key: string,
  ): (payload: unknown) => void {
    return (payload: unknown): void => {
      this.eventCounts.set(key, (this.eventCounts.get(key) ?? 0) + 1);
      try {
        const contactId = extractContactId(payload);
        this.eventLog.append({
          kind: "event",
          moduleId,
          name: eventName,
          payload,
          ...(contactId && { contactId }),
        });
      } catch (e) {
        log.error("failed to record event", { key, e });
      }
    };
  }

  /** Unsubscribes one event, calling `off` with the same extra args used to subscribe. Returns
   * false if it was not active. */
  unsubscribe(moduleId: string, eventName: string): boolean {
    const key = keyOf(moduleId, eventName);
    const sub = this.active.get(key);
    if (!sub) return false;

    const mod = registry.find((m) => m.id === moduleId);
    const off = mod?.events.find((e) => e.name === eventName)?.off;
    const client = this.gateway.getClient(moduleId);
    if (off && client && typeof client[off] === "function") {
      (client[off] as (h: unknown, ...rest: unknown[]) => void)(
        sub.handler,
        ...sub.extraArgs,
      );
    } else {
      // The handler stays attached to the live client; only the kit's own bookkeeping is
      // dropped. Say so, rather than letting isActive() report a detachment that never happened.
      log.warn(
        "no usable off method: the handler remains attached to the client and may keep firing",
        { moduleId, eventName, off },
      );
    }
    this.active.delete(key);
    return true;
  }

  /** Whether an event is currently subscribed. */
  isActive(moduleId: string, eventName: string): boolean {
    return this.active.has(keyOf(moduleId, eventName));
  }

  /** Active subscription keys in `moduleId:eventName` form. */
  activeKeys(): string[] {
    return [...this.active.keys()];
  }

  /**
   * Subscribes to every declared event on every non-interceptor module that can be subscribed
   * with no caller input. Interceptor-kind modules are opt-in and driven separately (by
   * `InterceptorManager` in a later task). Events declaring a required arg with no default are
   * SKIPPED, not registered dead: only the caller knows the scope value (e.g. which contactId
   * `onAIAgentContactReady` should watch). Returns the number of subscriptions now active.
   */
  subscribeAll(): number {
    for (const m of registry) {
      if (m.kind === "interceptor") continue;
      for (const e of m.events) {
        if (needsCallerArgs(e)) {
          log.info("skipping event that requires caller-supplied args", {
            moduleId: m.id,
            eventName: e.name,
          });
          continue;
        }
        this.subscribe(m.id, e.name);
      }
    }
    return this.active.size;
  }

  /**
   * Unsubscribes everything. Iterates the stored `{ moduleId, eventName }` tuples directly
   * rather than splitting the composite key string, so a future moduleId containing a colon
   * cannot be mis-parsed (amendment A11). Returns the number removed.
   *
   * Deliberately does NOT reset `counts()` — see {@link counts}.
   */
  unsubscribeAll(): number {
    const subs = [...this.active.values()];
    for (const sub of subs) this.unsubscribe(sub.moduleId, sub.eventName);
    return subs.length;
  }

  /**
   * Per-event delivery counts, CUMULATIVE for the lifetime of this manager. They are evidence of
   * what the SDK actually delivered during the session, so unsubscribing (individually or via
   * `unsubscribeAll`) does not zero them — a subscribe/unsubscribe/re-subscribe cycle keeps
   * accumulating onto the same tally. Construct a new manager for a fresh session tally.
   */
  counts(): Record<string, number> {
    return Object.fromEntries(this.eventCounts);
  }
}
